import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { PageHeader } from "../components/Crud";
import {
    Sparkles, PlayCircle, BookOpen, Mic, Layers, Brain, PenLine,
    Briefcase, FolderKanban, MessageSquareHeart, ClipboardList, Mail,
    HelpCircle, ChevronDown, ChevronRight, Compass,
} from "lucide-react";

const STUDENT_LINKS = [
    { icon: BookOpen, title: "Study Hub", desc: "Live recording, transcription and smart notes for every class.", to: "/study-hub" },
    { icon: Layers, title: "Missed Lecture Mode", desc: "Upload slides → AI-rebuilt lecture, gap analysis and revision toolkit.", to: "/study-hub" },
    { icon: Brain, title: "AI Tutor & Revision", desc: "Flashcards, quizzes and practice questions from your own materials.", to: "/study-hub" },
    { icon: PenLine, title: "Reference Checker", desc: "Open any project and use the Reference Checker for citations.", to: "/projects" },
];

const PRO_LINKS = [
    { icon: Briefcase, title: "Client Management", desc: "Contacts, contracts, payments, documents — all in the client profile.", to: "/clients" },
    { icon: FolderKanban, title: "Project Breakdown", desc: "Milestones, tasks and subtasks with auto-calculated progress.", to: "/projects" },
    { icon: MessageSquareHeart, title: "Client Memory", desc: "Open any client → Memory tab to see concerns, preferences and timeline.", to: "/clients" },
    { icon: ClipboardList, title: "AI Meeting Hub", desc: "Record or upload a call → smart notes + action items + follow-up.", to: "/clients" },
    { icon: Mail, title: "Follow-Up Generator", desc: "Drafted in your voice from meeting notes — copy or mailto in one click.", to: "/clients" },
];

const FAQS = [
    {
        q: "How does the free trial work?",
        a: "You get 7 days of full access from the moment you sign up — no card required. You can subscribe any time from Billing; otherwise you'll be prompted to add a card when the trial ends. Your data stays exactly where it is.",
    },
    {
        q: "Can I cancel any time?",
        a: "Yes. Go to Billing → Manage subscription and you'll land in the Stripe customer portal where you can cancel or update your card. Cancelling at the end of a billing cycle means you keep access until that cycle ends.",
    },
    {
        q: "Is my data safe?",
        a: "We use Stripe for payments (we never see your card), and all your content lives in our database with daily backups. Read the full Privacy Policy for retention details.",
    },
    {
        q: "How accurate is the AI transcription?",
        a: "We use OpenAI Whisper, which is industry-leading for English. For best results record in a quiet room and speak clearly. Long recordings are chunked automatically.",
    },
    {
        q: "Does Missed Lecture Mode work with PDFs?",
        a: "Yes — PPTX, PDF, DOCX, TXT and Markdown are all supported. The longer/clearer the deck, the better the reconstruction.",
    },
    {
        q: "Can I switch between Student and Professional?",
        a: "Your role determines which features show up. Switching isn't currently self-serve — drop us a line at hello@virtuallyunstoppable.app and we'll move you across.",
    },
];

export default function Help() {
    const { user } = useAuth();
    const isStudent = user?.role === "student";
    const links = isStudent ? STUDENT_LINKS : PRO_LINKS;
    return (
        <div data-testid="help-page">
            <PageHeader title="Help & quick tour" subtitle="everything in one place" />

            <VideoTourCard />

            <div className="grid lg:grid-cols-3 gap-6 mt-8">
                <div className="lg:col-span-2 space-y-6">
                    <section data-testid="help-feature-shortcuts">
                        <div className="flex items-baseline gap-2 mb-3">
                            <Compass size={16} className="text-[var(--brand-magenta)]" />
                            <h2 className="font-serif text-2xl">Where to start</h2>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                            {links.map((l) => (
                                <Link key={l.title} to={l.to}
                                      data-testid={`help-link-${l.title.replace(/\s+/g, "-").toLowerCase()}`}
                                      className="card-warm p-4 hover:-translate-y-0.5 hover:shadow-md transition block">
                                    <l.icon size={18} className="text-[var(--brand-purple)] mb-2" />
                                    <div className="font-serif text-lg">{l.title}</div>
                                    <div className="text-sm text-[var(--text-ink)]/70 mt-1">{l.desc}</div>
                                </Link>
                            ))}
                        </div>
                    </section>

                    <FAQ />
                </div>

                <aside className="space-y-4">
                    <ReplayTourCard />
                    <ContactCard />
                </aside>
            </div>
        </div>
    );
}

function VideoTourCard() {
    const url = process.env.REACT_APP_TOUR_VIDEO_URL;
    return (
        <div className="card-warm p-5 flex flex-col md:flex-row gap-5" data-testid="help-video-tour">
            <div className="md:w-2/5">
                <div className="font-script text-[var(--brand-orange)] text-xl">90-second tour</div>
                <h2 className="font-serif text-3xl">See it all in 90 seconds.</h2>
                <p className="text-sm text-[var(--text-ink)]/70 mt-2 leading-relaxed">
                    A guided walkthrough of how to capture, understand and finish work with Virtually Unstoppable.
                </p>
                <div className="text-xs text-[var(--text-ink)]/55 mt-3">
                    Pro tip: pair this with the <strong>Get Started</strong> checklist on your Dashboard.
                </div>
            </div>
            <div className="md:flex-1 aspect-video rounded-xl overflow-hidden relative" data-testid="help-video-frame">
                {url ? (
                    <iframe
                        src={url}
                        title="Virtually Unstoppable walkthrough"
                        className="w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        data-testid="help-video-iframe"
                    />
                ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-[var(--brand-purple)]/12 via-[var(--brand-magenta)]/12 to-[var(--brand-orange)]/12 grid place-items-center text-center p-6" data-testid="help-video-placeholder">
                        <PlayCircle size={56} className="text-[var(--brand-magenta)] mb-2" />
                        <div className="font-serif text-xl">Walkthrough video coming soon</div>
                        <div className="text-xs text-[var(--text-ink)]/60 mt-1">We&rsquo;re polishing the final cut.</div>
                    </div>
                )}
            </div>
        </div>
    );
}

function ReplayTourCard() {
    const replay = () => {
        localStorage.removeItem("vu_tour_done_v1");
        window.location.assign("/dashboard");
    };
    return (
        <div className="card-warm p-5" data-testid="help-replay-tour">
            <Sparkles size={18} className="text-[var(--brand-orange)] mb-1" />
            <div className="font-serif text-xl">Replay the in-app tour</div>
            <div className="text-sm text-[var(--text-ink)]/70 mt-1">
                Pops the 3-step guided tour back open on the Dashboard.
            </div>
            <button onClick={replay} data-testid="help-replay-btn" className="btn-primary text-sm mt-3 w-full">
                Replay tour
            </button>
        </div>
    );
}

function ContactCard() {
    return (
        <div className="card-warm p-5" data-testid="help-contact">
            <HelpCircle size={18} className="text-[var(--brand-purple)] mb-1" />
            <div className="font-serif text-xl">Still stuck?</div>
            <div className="text-sm text-[var(--text-ink)]/70 mt-1">
                We read every message. Average reply: under 24h on weekdays.
            </div>
            <a href="mailto:hello@virtuallyunstoppable.app" data-testid="help-contact-link"
               className="btn-ghost text-sm mt-3 inline-flex w-full justify-center">
                hello@virtuallyunstoppable.app
            </a>
            <div className="mt-3 flex justify-between text-xs text-[var(--text-ink)]/60">
                <Link to="/privacy" className="hover:text-[var(--brand-purple)]">Privacy</Link>
                <Link to="/terms" className="hover:text-[var(--brand-purple)]">Terms</Link>
                <Link to="/billing" className="hover:text-[var(--brand-purple)]">Billing</Link>
            </div>
        </div>
    );
}

function FAQ() {
    const [openIdx, setOpenIdx] = useState(0);
    return (
        <section data-testid="help-faq">
            <div className="flex items-baseline gap-2 mb-3">
                <HelpCircle size={16} className="text-[var(--brand-magenta)]" />
                <h2 className="font-serif text-2xl">Frequently asked</h2>
            </div>
            <div className="space-y-2">
                {FAQS.map((f, i) => {
                    const open = openIdx === i;
                    return (
                        <div key={i} className="card-warm overflow-hidden" data-testid={`faq-${i}`}>
                            <button
                                onClick={() => setOpenIdx(open ? -1 : i)}
                                className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-[rgba(255,107,53,0.04)]"
                                data-testid={`faq-toggle-${i}`}
                            >
                                {open ? <ChevronDown size={16} className="text-[var(--brand-orange)] shrink-0" />
                                      : <ChevronRight size={16} className="text-[var(--text-ink)]/55 shrink-0" />}
                                <span className="font-medium">{f.q}</span>
                            </button>
                            {open && (
                                <div className="px-4 pb-4 pt-1 text-sm text-[var(--text-ink)]/75 leading-relaxed" data-testid={`faq-answer-${i}`}>
                                    {f.a}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </section>
    );
}
